import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Button,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { API } from '@/services/api.service';
import {
  EQUITY_TYPES,
  PAYMENT_METHODS,
  date,
  errorText,
  money,
} from './format';

const { Text } = Typography;

const round2 = (v) => Math.round(Number(v) * 100) / 100;

/**
 * What happened in the quarter: capital paid in (Table 3), dividends voted or
 * paid (Table 5), and the value of the obligor's own holdings (Table 6). All
 * in USD; a payment entered in RSD with its day's rate is converted here.
 * APR's record of capital payments in the period is offered as rows.
 */
export default function TransactionsTab({
  data,
  companyId,
  period,
  locked,
  onSaved,
}) {
  const { t } = useTranslation('diReports');
  const [investments, setInvestments] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [table6, setTable6] = useState({
    holdingsValueUsd: null,
    holdingsRetainedUsd: null,
  });

  useEffect(() => {
    const r = data.report || {};
    setInvestments((r.investments || []).map((x, i) => ({ ...x, key: i })));
    setPayouts((r.payouts || []).map((x, i) => ({ ...x, key: i })));
    setTable6({
      holdingsValueUsd: r.holdingsValueUsd ?? null,
      holdingsRetainedUsd: r.holdingsRetainedUsd ?? null,
    });
  }, [data]);

  // S001… for direct owners, the country code for portfolio owners.
  const investorOptions = useMemo(
    () => [
      ...(data.form.owners || []).map((o) => ({
        value: o.code,
        label: `${o.code} · ${o.name}`,
      })),
      ...(data.form.portfolio || [])
        .filter((p) => p.countryCode)
        .map((p) => ({
          value: String(p.countryCode),
          label: `${p.countryCode} · ${t('owners.portfolio')}`,
        })),
    ],
    [data, t]
  );
  const monthOptions = data.months.map((m) => ({
    value: m,
    label: `${String(m).slice(4)}.${String(m).slice(0, 4)}`,
  }));

  const upd = (setter) => (key, patch) =>
    setter((l) => l.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const updInv = upd(setInvestments);
  const updPay = upd(setPayouts);

  // RSD amount with its rate gives USD; the USD field can also be typed directly.
  const changeRsd = (row, patch) => {
    const next = { ...row, ...patch };
    const usd =
      next.amountRsd && next.rate
        ? round2(next.amountRsd / next.rate)
        : next.valueUsd;
    updInv(row.key, { ...patch, valueUsd: usd });
  };

  const save = useMutation({
    mutationFn: () =>
      API.diReports.saveReport(companyId, period, {
        investments: investments.map(({ key: _key, ...x }) => x),
        payouts: payouts.map(({ key: _key, ...x }) => x),
        ...table6,
      }),
    onSuccess: (fresh) => {
      message.success(t('messages.saved'));
      onSaved(fresh);
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const addSuggestion = (s) =>
    setInvestments((l) => [
      ...l,
      {
        key: Date.now(),
        investor: s.investor || investorOptions[0]?.value || '',
        month: s.month,
        amountRsd: s.amountRsd,
        rate: s.rate,
        valueUsd: s.valueUsd,
        equityType: 20,
        paymentMethod: s.paymentMethod,
        sharePct: null,
        isin: '',
        custodianMb: '',
      },
    ]);

  const invColumns = [
    {
      title: t('tx.investor'),
      dataIndex: 'investor',
      width: 200,
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={investorOptions}
          onChange={(x) => updInv(r.key, { investor: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('tx.month'),
      dataIndex: 'month',
      width: 110,
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={monthOptions}
          onChange={(x) => updInv(r.key, { month: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('tx.amountRsd'),
      dataIndex: 'amountRsd',
      width: 150,
      render: (v, r) => (
        <InputNumber
          disabled={locked}
          value={v}
          onChange={(x) => changeRsd(r, { amountRsd: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('tx.rate'),
      dataIndex: 'rate',
      width: 110,
      render: (v, r) => (
        <InputNumber
          disabled={locked}
          value={v}
          step={0.0001}
          onChange={(x) => changeRsd(r, { rate: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('tx.valueUsd'),
      dataIndex: 'valueUsd',
      width: 150,
      render: (v, r) => (
        <InputNumber
          disabled={locked}
          value={v}
          onChange={(x) => updInv(r.key, { valueUsd: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('tx.equityType'),
      dataIndex: 'equityType',
      width: 190,
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={EQUITY_TYPES.map((x) => ({
            value: x,
            label: `${x} — ${t(`equityType.${x}`)}`,
          }))}
          onChange={(x) => updInv(r.key, { equityType: x })}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
        />
      ),
    },
    {
      title: t('tx.paymentMethod'),
      dataIndex: 'paymentMethod',
      width: 160,
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={PAYMENT_METHODS.map((x) => ({
            value: x,
            label: `${x} — ${t(`paymentMethod.${x}`)}`,
          }))}
          onChange={(x) => updInv(r.key, { paymentMethod: x })}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
        />
      ),
    },
    {
      title: t('tx.sharePct'),
      dataIndex: 'sharePct',
      width: 90,
      render: (v, r) => (
        <InputNumber
          disabled={locked}
          value={v}
          min={-100}
          max={100}
          onChange={(x) => updInv(r.key, { sharePct: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 46,
      render: (_, r) =>
        !locked && (
          <Button
            shape="circle"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() =>
              setInvestments((l) => l.filter((x) => x.key !== r.key))
            }
          />
        ),
    },
  ];

  const payAmount = (field) => ({
    title: t(`tx.${field}`),
    dataIndex: field,
    width: 140,
    render: (v, r) => (
      <InputNumber
        disabled={locked}
        value={v}
        onChange={(x) => updPay(r.key, { [field]: x })}
        style={{ width: '100%' }}
      />
    ),
  });
  const payColumns = [
    {
      ...invColumns[0],
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={investorOptions}
          onChange={(x) => updPay(r.key, { investor: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      ...invColumns[1],
      render: (v, r) => (
        <Select
          disabled={locked}
          value={v}
          options={monthOptions}
          onChange={(x) => updPay(r.key, { month: x })}
          style={{ width: '100%' }}
        />
      ),
    },
    payAmount('declaredDividend'),
    payAmount('paidDividend'),
    payAmount('declaredRetained'),
    payAmount('paidRetained'),
    {
      title: '',
      key: 'remove',
      width: 46,
      render: (_, r) =>
        !locked && (
          <Button
            shape="circle"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => setPayouts((l) => l.filter((x) => x.key !== r.key))}
          />
        ),
    },
  ];

  const unused = (data.suggestedInvestments || []).filter(
    (s) =>
      !investments.some(
        (i) =>
          i.month === s.month &&
          Math.abs((i.amountRsd || 0) - (s.amountRsd || 0)) < 1
      )
  );
  const hasHoldings = (data.profile.holdings || []).length > 0;
  // 6a left empty = the nominal value of the stakes, worked out by the server.
  const nominal = data.table6Nominal;
  const nominalUsd = nominal?.valueUsd ?? null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Text strong>{t('tx.t3Title')}</Text>
        <div className="mb-2 text-xs opacity-70">{t('tx.t3Help')}</div>
        {unused.length > 0 && !locked && (
          <Alert
            type="warning"
            showIcon
            className="!static mb-2"
            message={
              <Space direction="vertical" size={2}>
                {unused.map((s, i) => (
                  <span key={i}>
                    {t('tx.aprPayment', {
                      date: date(s.date),
                      owner: s.owner,
                      amount: money(s.amount),
                      currency: s.currency,
                      usd: money(s.valueUsd),
                    })}{' '}
                    <a onClick={() => addSuggestion(s)}>{t('actions.add')}</a>
                  </span>
                ))}
              </Space>
            }
          />
        )}
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          columns={invColumns}
          dataSource={investments}
          scroll={{ x: 1150 }}
          locale={{ emptyText: t('tx.none') }}
          footer={() =>
            !locked && (
              <Button
                shape="round"
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  setInvestments((l) => [
                    ...l,
                    {
                      key: Date.now(),
                      investor: investorOptions[0]?.value || '',
                      month: data.months[2],
                      amountRsd: null,
                      rate: null,
                      valueUsd: null,
                      equityType: 20,
                      paymentMethod: 10,
                      sharePct: null,
                      isin: '',
                      custodianMb: '',
                    },
                  ])
                }
              >
                {t('tx.add')}
              </Button>
            )
          }
        />
      </div>

      <div>
        <Text strong>{t('tx.t5Title')}</Text>
        <div className="mb-2 text-xs opacity-70">{t('tx.t5Help')}</div>
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          columns={payColumns}
          dataSource={payouts}
          scroll={{ x: 1000 }}
          locale={{ emptyText: t('tx.none') }}
          footer={() =>
            !locked && (
              <Button
                shape="round"
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  setPayouts((l) => [
                    ...l,
                    {
                      key: Date.now(),
                      investor: investorOptions[0]?.value || '',
                      month: data.months[2],
                      declaredDividend: null,
                      paidDividend: null,
                      declaredRetained: null,
                      paidRetained: null,
                    },
                  ])
                }
              >
                {t('tx.add')}
              </Button>
            )
          }
        />
      </div>

      <div>
        <Text strong>{t('tx.t6Title')}</Text>
        <div className="mb-2 text-xs opacity-70">
          {hasHoldings ? t('tx.t6Required') : t('tx.t6Help')}
        </div>
        {nominal && (
          <div className="mb-2 text-xs opacity-70">
            {nominalUsd !== null
              ? t('tx.t6Nominal', {
                  parts: nominal.parts
                    .map(
                      (p) =>
                        `${p.name || p.mb}: ${money(p.share)}% × ${money(p.capitalRsd, 0)} RSD`
                    )
                    .join('; '),
                  rsd: money(nominal.valueRsd),
                  usd: money(nominalUsd),
                })
              : t('tx.t6NominalMissing')}
          </div>
        )}
        <Space wrap>
          <span>{t('tx.holdingsValueUsd')}</span>
          <InputNumber
            disabled={locked}
            status={
              hasHoldings &&
              !(table6.holdingsValueUsd > 0) &&
              !(nominalUsd > 0)
                ? 'error'
                : undefined
            }
            placeholder={nominalUsd !== null ? money(nominalUsd) : undefined}
            value={table6.holdingsValueUsd}
            onChange={(v) =>
              setTable6((x) => ({ ...x, holdingsValueUsd: v ?? null }))
            }
            style={{ width: 180 }}
          />
          <span>{t('tx.holdingsRetainedUsd')}</span>
          <InputNumber
            disabled={locked}
            value={table6.holdingsRetainedUsd}
            onChange={(v) =>
              setTable6((x) => ({ ...x, holdingsRetainedUsd: v ?? null }))
            }
            style={{ width: 180 }}
          />
        </Space>
      </div>

      {!locked && (
        <div className="flex justify-end">
          <Button
            shape="round"
            type="primary"
            icon={<SaveOutlined />}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
